import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, TextField, MenuItem, Grid,
  Alert, CircularProgress, Chip, Divider, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Switch,
  FormControlLabel, Stepper, Step, StepLabel, Autocomplete
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';

const API = '/api/contabilidad';

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const ANIOS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

const PASOS = ['Selección', 'Vista previa', 'Configuración', 'Ejecutar cierre'];

export default function CierreContable() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [anio, setAnio] = useState(new Date().getFullYear() - 1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paso, setPaso] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Configuración cierre
  const [cuentaResultadoId, setCuentaResultadoId] = useState('');
  const [cuentaUtilidadesId, setCuentaUtilidadesId] = useState('');
  const [cuentaPerdidasId, setCuentaPerdidasId] = useState('');
  const [generarApertura, setGenerarApertura] = useState(true);

  // Cuentas disponibles
  const [cuentasPatrimonio, setCuentasPatrimonio] = useState([]);
  const [cuentasResultado, setCuentasResultado] = useState([]);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1&tipo_cuenta=PATRIMONIO`).then(r => setCuentasPatrimonio(r.data)).catch(() => {});
    // También cuentas 59xxxx para resultado del ejercicio
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1&buscar=59`).then(r => setCuentasResultado(r.data)).catch(() => {});
  }, [empresaId]);

  async function handlePreview() {
    if (!empresaId || !anio) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const r = await axios.get(`${API}/cierre-anual/preview`, { params: { empresa_id: empresaId, anio } });
      setPreview(r.data);
      setPaso(1);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al obtener preview');
    }
    setLoading(false);
  }

  async function handleEjecutar() {
    if (!window.confirm(`¿Ejecutar el CIERRE CONTABLE ANUAL del ejercicio ${anio}? Esta operación generará los asientos de cierre y no se puede deshacer fácilmente.`)) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/cierre-anual/ejecutar`, {
        empresa_id: empresaId,
        anio,
        cuenta_resultado_id: cuentaResultadoId || undefined,
        cuenta_utilidades_id: cuentaUtilidadesId || undefined,
        cuenta_perdidas_id: cuentaPerdidasId || undefined,
        generar_apertura: generarApertura,
      });
      setResult(r.data);
      setPaso(3);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al ejecutar cierre');
    }
    setLoading(false);
  }

  async function handleRevertir() {
    if (!window.confirm(`¿Revertir el cierre del año ${anio}? Los asientos CA y AP del año serán anulados.`)) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/cierre-anual/revertir`, { empresa_id: empresaId, anio });
      alert(`Cierre revertido. ${r.data.asientos_anulados} asientos anulados.`);
      setPreview(null);
      setResult(null);
      setPaso(0);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al revertir');
    }
    setLoading(false);
  }

  const esUtilidad = preview && preview.resultado_neto >= 0;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', mt: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={1}>Cierre Contable Anual</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Genera los asientos de cierre de cuentas de resultado (ingresos y gastos) y determina la utilidad o pérdida del ejercicio.
        Opcionalmente genera el asiento de apertura del año siguiente.
      </Typography>

      <Stepper activeStep={paso} sx={{ mb: 3 }}>
        {PASOS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Paso 0: Selección */}
      {paso === 0 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm="auto">
              <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
                {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField select fullWidth size="small" label="Año Fiscal" value={anio} onChange={e => setAnio(e.target.value)} sx={{ minWidth: '20em' }}>
                {ANIOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Button fullWidth variant="contained" startIcon={<SearchIcon />} disabled={!empresaId || loading} onClick={handlePreview}>
                Analizar ejercicio {anio}
              </Button>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 3 }} icon={<WarningAmberIcon />}>
            <Typography variant="body2" fontWeight="bold">Requisitos antes del cierre anual:</Typography>
            <Typography variant="body2">• Todos los períodos del año deben estar en estado CERRADO</Typography>
            <Typography variant="body2">• Todos los asientos del año deben estar cuadrados (debe = haber)</Typography>
            <Typography variant="body2">• No debe existir un cierre anterior para el mismo año</Typography>
          </Alert>
        </Paper>
      )}

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      {/* Paso 1: Vista previa */}
      {paso >= 1 && preview && !result && (
        <Box>
          <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">Resumen del Ejercicio {anio}</Typography>
                <Typography variant="body2" color="text.secondary">Empresa: {empresas.find(e => e.id == empresaId)?.razon_social}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, justifyContent: { sm: 'flex-end' }, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" onClick={() => setPaso(0)}>Cambiar selección</Button>
                {preview.ya_cerrado && (
                  <Button variant="outlined" color="error" size="small" startIcon={<UndoIcon />} onClick={handleRevertir}>Revertir cierre</Button>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total Ingresos</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">S/ {fmt(preview.total_ingresos)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total Gastos/Costos</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">S/ {fmt(preview.total_gastos)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: esUtilidad ? '#e3f2fd' : '#fff3e0', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Resultado del Ejercicio</Typography>
                  <Typography variant="h6" fontWeight="bold" color={esUtilidad ? 'info.main' : 'warning.main'}>
                    {esUtilidad ? 'UTILIDAD' : 'PÉRDIDA'}: S/ {fmt(Math.abs(preview.resultado_neto))}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Períodos</Typography>
                  <Typography variant="body1">
                    <Chip label={`${preview.periodosCerrados} cerrados`} color="success" size="small" sx={{ mr: 0.5 }} />
                    {preview.periodosAbiertos > 0 && <Chip label={`${preview.periodosAbiertos} abiertos`} color="warning" size="small" />}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {preview.ya_cerrado && (
              <Alert severity="warning" sx={{ mt: 2 }}>El año {anio} ya tiene asientos de cierre registrados. Puedes revertirlos y volver a ejecutar el cierre.</Alert>
            )}
            {preview.periodosAbiertos > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Hay {preview.periodosAbiertos} período(s) sin cerrar. Debes cerrar todos los períodos antes de ejecutar el cierre anual.
                <Typography variant="body2">Períodos abiertos: {preview.periodos.filter(p => p.estado !== 'CERRADO').map(p => p.nombre || p.periodo).join(', ')}</Typography>
              </Alert>
            )}
          </Paper>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 300, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow sx={{ bgcolor: '#e8f5e9' }}>
                    <TableCell colSpan={2}><Typography fontWeight="bold" color="success.main">Ingresos a cerrar ({preview.ingresos.length} cuentas)</Typography></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {preview.ingresos.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>{r.codigo} — {r.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>S/ {fmt(r.saldo)}</TableCell>
                      </TableRow>
                    ))}
                    {preview.ingresos.length === 0 && <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>Sin movimientos</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 300, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow sx={{ bgcolor: '#ffebee' }}>
                    <TableCell colSpan={2}><Typography fontWeight="bold" color="error.main">Gastos/Costos a cerrar ({preview.gastos.length} cuentas)</Typography></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {preview.gastos.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>{r.codigo} — {r.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>S/ {fmt(r.saldo)}</TableCell>
                      </TableRow>
                    ))}
                    {preview.gastos.length === 0 && <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>Sin movimientos</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {/* Paso 2: Configuración */}
          <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Configuración de cuentas destino</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={[...cuentasResultado, ...cuentasPatrimonio]}
                  getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                  onChange={(_, v) => setCuentaResultadoId(v?.id || '')}
                  renderInput={params => <TextField {...params} size="small" label="Cuenta Resultado del Ejercicio (59x)" helperText="Deja vacío para usar la configurada en parámetros" />}
                  filterOptions={(opts, { inputValue }) => opts.filter(o =>
                    o.codigo?.includes(inputValue) || o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={cuentasPatrimonio}
                  getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                  onChange={(_, v) => setCuentaUtilidadesId(v?.id || '')}
                  renderInput={params => <TextField {...params} size="small" label={`Cuenta destino ${esUtilidad ? 'Utilidades (591x)' : 'Pérdidas (592x)'}`} helperText="Cuenta donde se traslada la utilidad/pérdida neta" />}
                  filterOptions={(opts, { inputValue }) => opts.filter(o =>
                    o.codigo?.includes(inputValue) || o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={generarApertura} onChange={e => setGenerarApertura(e.target.checked)} color="primary" />}
                  label={`Generar asiento de apertura del ejercicio ${parseInt(anio) + 1}`}
                />
                <Typography variant="caption" color="text.secondary" display="block" ml={4}>
                  El asiento de apertura traslada los saldos de cuentas de balance (activo, pasivo, patrimonio) al inicio del año {parseInt(anio) + 1}.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<LockIcon />}
              disabled={!preview.puede_cerrar || loading}
              onClick={handleEjecutar}
              sx={{ px: 4 }}
            >
              {preview.puede_cerrar ? `Ejecutar Cierre Anual ${anio}` : 'No disponible (ver requisitos)'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Paso 3: Resultado */}
      {paso === 3 && result && (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="success.main" mb={1}>
            Cierre del Ejercicio {result.anio} completado
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Resultado: {result.resultado_neto >= 0 ? 'UTILIDAD' : 'PÉRDIDA'} S/ {fmt(Math.abs(result.resultado_neto))}
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {result.asientos_generados.map((a, i) => (
              <Grid item key={i}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${a.tipo.replace('_', ' ')} — Asiento #${a.movcab_id}`}
                  color="success" variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => { setPaso(0); setPreview(null); setResult(null); }}>Nuevo cierre</Button>
            <Button variant="outlined" color="error" startIcon={<UndoIcon />} onClick={handleRevertir}>Revertir este cierre</Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

